<template>
    <div class="custom-multiselect"
         :class="{ 'is-open': isOpen }"
         :name="name">
        <div :id="name"
             class="custom-multiselect-selection"
             @click="toggleDropdown">
            <template v-if="multiple">
                <div v-if="selected.length"
                     class="custom-multiselect-tags">
                    <span v-for="item in visibleTags"
                          :key="getOptionKey(item)"
                          class="custom-multiselect-tag">
                        {{ getOptionLabel(item) }}
                        <button type="button"
                                class="btn-close"
                                aria-label="Remove"
                                @click.stop="removeItem(item)">
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </span>
                    <span v-if="remainingTagsCount > 0"
                          class="custom-multiselect-tag remaining">
                        +{{ remainingTagsCount }}
                    </span>
                </div>
                <span v-else
                      class="custom-multiselect-placeholder">{{ placeholder }}</span>
            </template>
            <template v-else>
                <span :class="{ 'custom-multiselect-placeholder': !selected.length }">
                    {{ selected.length ? getOptionLabel(selected[0]) : placeholder }}
                </span>
            </template>
            <div class="custom-multiselect-actions">
                <button v-if="selected.length && showResetButton"
                        type="button"
                        class="btn-clear"
                        aria-label="Clear selection"
                        @click.stop="clearSelection">
                    <span aria-hidden="true">
                        <font-awesome-icon :icon="icons.faRotateLeft"
                                           size="sm" />
                    </span>
                </button>
                <font-awesome-icon class="custom-multiselect-caret"
                                   :icon="icons.faChevronDown" />
            </div>
        </div>

        <div v-show="isOpen"
             class="custom-multiselect-dropdown">
            <div v-if="showSearch"
                 class="custom-multiselect-search">
                <input ref="searchInput"
                       v-model="searchQuery"
                       type="text"
                       :placeholder="searchPlaceholder"
                       @click.stop>
                <button v-if="multiple"
                        type="button"
                        class="custom-multiselect-toggle-all"
                        @click.stop="toggleAll">
                    {{ areAllFiltered ? $t('actions.unselect_all') : $t('actions.select_all') }}
                </button>
            </div>
            <div class="custom-multiselect-options">
                <template v-if="isGrouped">
                    <div v-for="group in filteredOptions"
                         :key="group[groupBy]"
                         class="custom-multiselect-group">
                        <div class="custom-multiselect-group-label"
                             @click="toggleGroup(group[groupBy])">
                            <span>{{ group[groupBy] }}: {{ group.options.length }}</span>
                            <font-awesome-icon :icon="isGroupCollapsed(group[groupBy]) ? icons.faChevronDown : icons.faChevronUp" />
                        </div>
                        <div v-show="!isGroupCollapsed(group[groupBy])">
                            <div v-for="option in group.options"
                                 :key="getOptionKey(option)"
                                 class="custom-multiselect-option"
                                 :class="{ 'is-selected': isSelected(option) }"
                                 @click="toggleOption(option)">
                                <b-form-checkbox v-if="multiple"
                                                 :checked="isSelected(option)"
                                                 style="pointer-events: none;">
                                    {{ getOptionLabel(option) }}
                                </b-form-checkbox>
                                <span v-else>
                                    {{ getOptionLabel(option) }}
                                </span>
                            </div>
                        </div>
                    </div>
                </template>
                <template v-else>
                    <div v-for="option in filteredOptions"
                         :key="getOptionKey(option)"
                         class="custom-multiselect-option"
                         :class="{ 'is-selected': isSelected(option) }"
                         @click="toggleOption(option)">
                        <b-form-checkbox v-if="multiple"
                                         :checked="isSelected(option)"
                                         style="pointer-events: none;">
                            {{ getOptionLabel(option) }}
                        </b-form-checkbox>
                        <span v-else>
                            {{ getOptionLabel(option) }}
                        </span>
                    </div>
                </template>
            </div>
        </div>
    </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faChevronDown, faRotateLeft, faChevronUp } from '@fortawesome/pro-solid-svg-icons'
import { BFormCheckbox } from 'bootstrap-vue'

export default {
  name: 'CustomMultiSelect',

  components: {
    FontAwesomeIcon,
    BFormCheckbox
  },

  props: {
    options: {
      type: Array,
      required: true
    },
    name: {
      type: String,
      default: ''
    },
    label: {
      type: String,
      default: ''
    },
    value: {
      type: Array,
      default: () => []
    },
    trackBy: {
      type: String,
      default: 'id'
    },
    placeholder: {
      type: String,
      default () {
        return this.$t('actions.select_options')
      }
    },
    searchPlaceholder: {
      type: String,
      default () {
        return this.$t('actions.search') + '...'
      }
    },
    maxVisibleTags: {
      type: Number,
      default: 2
    },
    multiple: {
      type: Boolean,
      default: true
    },
    showResetButton: {
      type: Boolean,
      default: true
    },
    groupBy: {
      type: String,
      default: null
    }
  },

  data () {
    return {
      isOpen: false,
      searchQuery: '',
      selected: this.value,
      icons: {
        faChevronDown,
        faRotateLeft,
        faChevronUp
      },
      collapsedGroups: {}
    }
  },

  computed: {
    isGrouped () {
      return !!this.groupBy
    },

    showSearch () {
      return this.options.length > 5
    },

    filteredOptions () {
      // When grouped, ensure options are in the correct format. If not, return empty to prevent errors.
      if (this.isGrouped && this.options.length && !this.options[0].options) {
        return []
      }

      if (!this.searchQuery) {
        return this.options
      }

      const query = this.searchQuery.toLowerCase()

      if (!this.isGrouped) {
        return this.options.filter(option =>
          this.getOptionLabel(option).toLowerCase().includes(query)
        )
      }

      const filtered = []
      this.options.forEach(group => {
        // A group must have an `options` array to be valid.
        if (!group.options) return

        const groupOptions = group.options.filter(option =>
          this.getOptionLabel(option).toLowerCase().includes(query)
        )

        if (groupOptions.length) {
          filtered.push({
            ...group,
            options: groupOptions
          })
        }
      })
      return filtered
    },

    areAllFiltered () {
      const allFilteredOptions = this.isGrouped
        ? this.filteredOptions.flatMap(g => g.options)
        : this.filteredOptions

      if (!allFilteredOptions.length) return false
      return allFilteredOptions.every(option => this.isSelected(option))
    },

    visibleTags () {
      return this.selected.slice(0, this.maxVisibleTags)
    },

    remainingTagsCount () {
      return Math.max(0, this.selected.length - this.maxVisibleTags)
    }
  },

  watch: {
    value: {
      handler (newVal) {
        this.selected = newVal
      },
      deep: true
    },
    isOpen (newVal) {
      if (newVal) {
        this.$nextTick(() => {
          if (this.showSearch) {
            this.$refs.searchInput?.focus()
          }
        })
        document.addEventListener('click', this.handleClickOutside)
      } else {
        document.removeEventListener('click', this.handleClickOutside)
      }
    },
    options: {
      handler () {
        this.searchQuery = ''
      },
      deep: true
    }
  },

  beforeDestroy () {
    document.removeEventListener('click', this.handleClickOutside)
  },

  methods: {
    toggleGroup (groupLabel) {
      this.$set(this.collapsedGroups, groupLabel, !this.collapsedGroups[groupLabel])
    },

    isGroupCollapsed (groupLabel) {
      return !!this.collapsedGroups[groupLabel]
    },

    toggleDropdown () {
      this.isOpen = !this.isOpen
    },

    toggleAll () {
      const allFilteredOptions = this.isGrouped
        ? this.filteredOptions.flatMap(g => g.options)
        : this.filteredOptions

      if (this.areAllFiltered) {
        // Unselect all filtered options
        const filteredKeys = new Set(allFilteredOptions.map(this.getOptionKey))
        this.selected = this.selected.filter(item => !filteredKeys.has(this.getOptionKey(item)))
      } else {
        // Select all filtered options
        const selectedKeys = new Set(this.selected.map(this.getOptionKey))
        const newSelected = [...this.selected]
        allFilteredOptions.forEach(option => {
          if (!selectedKeys.has(this.getOptionKey(option))) {
            newSelected.push(option)
          }
        })
        this.selected = newSelected
      }
      this.$emit('input', this.selected)
    },

    handleClickOutside (event) {
      // Check if the clicked element or any of its parents is a label with matching 'for' attribute
      let element = event.target
      while (element) {
        if (element.tagName === 'LABEL' && element.getAttribute('for') === this.name) {
          return
        }
        element = element.parentElement
      }

      if (!this.$el.contains(event.target)) {
        this.isOpen = false
      }
    },

    getOptionLabel (option) {
      return option[this.label]
    },

    getOptionKey (option) {
      return option[this.trackBy]
    },

    isSelected (option) {
      return this.selected.some(item => this.getOptionKey(item) === this.getOptionKey(option))
    },

    toggleOption (option) {
      if (this.multiple) {
        const index = this.selected.findIndex(item => this.getOptionKey(item) === this.getOptionKey(option))
        if (index === -1) {
          this.selected = [...this.selected, option]
        } else {
          this.selected = this.selected.filter(item => this.getOptionKey(item) !== this.getOptionKey(option))
        }
      } else {
        if (this.selected.length && this.getOptionKey(this.selected[0]) === this.getOptionKey(option)) {
          this.selected = []
        } else {
          this.selected = [option]
        }
        this.isOpen = false
      }
      this.$emit('input', this.selected)
    },

    removeItem (item) {
      this.selected = this.selected.filter(i => this.getOptionKey(i) !== this.getOptionKey(item))
      this.$emit('input', this.selected)
    },

    clearSelection () {
      this.selected = []
      this.$emit('input', this.selected)
      this.$emit('clear')
    }
  }
}
</script>
